import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { useShop } from "@/context/ShopContext";
import { inr } from "@/lib/api";

export const productBadges = (product) => {
  const care = (product.specs?.Care || "").toLowerCase();
  const badges = [];
  if (product.original_price > product.price) badges.push({ label: "Sale", cls: "bg-terra text-white" });
  if (product.featured) badges.push({ label: "Bestseller", cls: "bg-espresso text-cream" });
  if (care.includes("microwave")) badges.push({ label: "Microwave safe", cls: "border border-linen bg-white/90 text-clay" });
  if (care.includes("dishwasher")) badges.push({ label: "Dishwasher safe", cls: "border border-linen bg-white/90 text-clay" });
  return badges;
};

const ProductCard = ({ product }) => {
  const { addToCart, setCartOpen } = useShop();
  const soldOut = product.stock === 0;
  const lowStock = !soldOut && product.stock < 8;
  const badges = productBadges(product);
  const overlayBadges = badges.filter((b) => b.label === "Sale" || b.label === "Bestseller");
  const careBadges = badges.filter((b) => b.label !== "Sale" && b.label !== "Bestseller");

  const handleAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, 1);
    toast.success(`${product.name} added to cart`);
    setCartOpen(true);
  };

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid="product-card-item"
      className="group block transition-transform duration-300 ease-out hover:-translate-y-1"
    >
      <div className="relative aspect-[4/5] overflow-hidden rounded-2xl bg-sand">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
        />
        <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
          {overlayBadges.map((b) => (
            <span key={b.label} className={`rounded-full px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-widest ${b.cls}`}>
              {b.label}
            </span>
          ))}
        </div>
        {soldOut && (
          <div className="absolute inset-0 flex items-center justify-center bg-espresso/55">
            <span className="rounded-full border border-cream/40 px-4 py-1.5 font-mono text-xs uppercase tracking-[0.2em] text-cream">
              Sold out
            </span>
          </div>
        )}
        {!soldOut && (
          <button
            data-testid="product-add-to-cart-button"
            onClick={handleAdd}
            aria-label={`Add ${product.name} to cart`}
            className="absolute bottom-3 right-3 flex h-11 w-11 translate-y-2 items-center justify-center rounded-full bg-espresso text-cream opacity-0 transition-all duration-300 hover:bg-terra group-hover:translate-y-0 group-hover:opacity-100 active:scale-95"
          >
            <Plus className="h-5 w-5" />
          </button>
        )}
      </div>
      <div className="mt-4 px-0.5">
        <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-terra">{product.category}</p>
        <h3 className="mt-1 font-serif text-lg leading-snug text-espresso">{product.name}</h3>
        <div className="mt-1.5 flex items-baseline gap-2">
          <span className="font-mono text-base font-semibold text-espresso">{inr(product.price)}</span>
          {product.original_price > product.price && (
            <span className="font-mono text-xs text-clay/50 line-through">{inr(product.original_price)}</span>
          )}
          {lowStock && <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-ochre">Only {product.stock} left</span>}
        </div>
        {careBadges.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {careBadges.map((b) => (
              <span key={b.label} className={`rounded-full px-2.5 py-0.5 font-mono text-[9px] uppercase tracking-widest ${b.cls}`}>
                {b.label}
              </span>
            ))}
          </div>
        )}
      </div>
    </Link>
  );
};

export default ProductCard;
